import React, { useState } from "react";
import SourceChip from "../section/SourceChip.jsx";
import api from "../../api/client.js";
import AutoTextarea from "../AutoTextarea.jsx";

/**
 * RoutineEditor — Her Day, by hand.
 *
 * Routines used to arrive only from ISP ingestion, so a client with no ISP
 * had an empty Her Day and nothing to click. This is the goals editor's
 * shape with a routine's fields: a time of day, a title, a short list of
 * steps (each with an optional note), and what helps the transition.
 *
 * Rows are grouped by time of day in the enum's order so the page reads as
 * a day. Hand-added routines carry no source chip; ingested ones keep theirs.
 */

const TIMES = ["morning", "midday", "afternoon", "evening", "night", "weekly"];
const TIME_LABELS = {
  morning: "Morning", midday: "Midday", afternoon: "Afternoon",
  evening: "Evening", night: "Night", weekly: "Weekly",
};

// Steps start empty: a routine may be a time and a title alone ("Morning
// medications") when the steps themselves would be confidential (C6).
const EMPTY = { time_of_day: "morning", title: "", steps: [], transition_notes: "" };

function StepsField({ steps, onChange }) {
  const set = (i, k, v) => onChange(steps.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div style={styles.steps}>
      {steps.map((s, i) => (
        <div key={i} style={styles.stepRow}>
          <span style={styles.stepNum}>{i + 1}</span>
          <div style={styles.stepFields}>
            <input style={styles.input} value={s.text} placeholder="What happens"
                   aria-label={`Step ${i + 1}`}
                   onChange={e => set(i, "text", e.target.value)} />
            <input style={{ ...styles.input, fontSize: "var(--text-xs)" }} value={s.notes || ""}
                   placeholder="Note (optional)" aria-label={`Step ${i + 1} note`}
                   onChange={e => set(i, "notes", e.target.value)} />
          </div>
          <div style={styles.stepBtns}>
            <button type="button" style={styles.tiny} onClick={() => move(i, -1)} aria-label="Move up" disabled={i === 0}>↑</button>
            <button type="button" style={styles.tiny} onClick={() => move(i, 1)} aria-label="Move down" disabled={i === steps.length - 1}>↓</button>
            <button type="button" style={styles.tiny} onClick={() => onChange(steps.filter((_, j) => j !== i))}
                    aria-label="Remove step">✕</button>
          </div>
        </div>
      ))}
      <button type="button" style={styles.addStep} onClick={() => onChange([...steps, { text: "", notes: "" }])}>+ Step</button>
    </div>
  );
}

function toBody(form) {
  return {
    time_of_day: form.time_of_day,
    title: form.title.trim(),
    steps: form.steps.filter(s => s.text.trim()).map(s => ({ text: s.text.trim(), notes: (s.notes || "").trim() || null })),
    transition_notes: form.transition_notes.trim() || null,
  };
}

function fromRoutine(r) {
  return {
    time_of_day: r.time_of_day,
    title: r.title,
    steps: (r.steps || []).map(s => ({ text: s.text || "", notes: s.notes || "" })),
    transition_notes: r.transition_notes || "",
  };
}

function RoutineForm({ initial, onSubmit, onCancel, submitLabel, saving }) {
  const [form, setForm] = useState(initial);
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const valid = !!form.title.trim();   // steps are optional
  return (
    <div style={styles.editCard}>
      <div style={styles.twoCol}>
        <div style={styles.field}>
          <label style={styles.label}>Time of day</label>
          <select style={styles.input} value={form.time_of_day} onChange={f("time_of_day")}>
            {TIMES.map(t => <option key={t} value={t}>{TIME_LABELS[t]}</option>)}
          </select>
        </div>
        <div style={{ ...styles.field, flex: 2 }}>
          <label style={styles.label}>Title <span style={{ color: "var(--color-critical)" }}>*</span></label>
          <input style={styles.input} value={form.title} onChange={f("title")} placeholder="e.g. Bus home" />
        </div>
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Steps <span style={{ color: "var(--color-text-tertiary)", fontWeight: 400 }}>(optional — leave empty to show the title alone)</span></label>
        <StepsField steps={form.steps} onChange={steps => setForm(p => ({ ...p, steps }))} />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>What helps the transition</label>
        <AutoTextarea style={styles.textarea} rows={2} value={form.transition_notes} onChange={f("transition_notes")}
                  placeholder="e.g. Give her the headphones before the bus." />
      </div>
      <div style={styles.editActions}>
        <button style={styles.saveBtn} onClick={() => onSubmit(toBody(form))} disabled={saving || !valid}>
          {saving ? "Saving…" : submitLabel}
        </button>
        <button style={styles.cancelBtn} onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

function RoutineRow({ routine, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  async function save(body) {
    setSaving(true);
    try {
      await api.patch(`/routines/${routine.id}`, body);
      onSave({ ...routine, ...body, steps: body.steps.map((s, i) => ({ order: i, ...s })) });
      setEditing(false);
    } catch (e) {
      alert(e.body?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirm("Delete this routine?")) return;
    await api.delete(`/routines/${routine.id}`);
    onDelete(routine.id);
  }

  if (editing) {
    return <RoutineForm initial={fromRoutine(routine)} onSubmit={save}
                        onCancel={() => setEditing(false)} submitLabel="Save" saving={saving} />;
  }
  return (
    <div style={styles.row}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={styles.rowTitle}>{routine.title}</p>
        <p style={styles.rowMeta}>{routine.steps?.length || 0} step{routine.steps?.length === 1 ? "" : "s"}{routine.transition_notes ? " · transition note" : ""}</p>
      </div>
      <SourceChip source={routine.source} />
      <div style={styles.actions}>
        <button style={styles.editBtn} onClick={() => setEditing(true)}>Edit</button>
        <button style={styles.deleteBtn} onClick={remove} aria-label="Delete routine">✕</button>
      </div>
    </div>
  );
}

export default function RoutineEditor({ clientId, initialRoutines = [], onRefresh }) {
  const [routines, setRoutines] = useState(initialRoutines);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  async function add(body) {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/sections/day/routines`, body);
      setAdding(false);
      onRefresh?.();
    } catch (e) {
      alert(e.body?.error || "Add failed");
    } finally {
      setSaving(false);
    }
  }

  const groups = TIMES.map(t => [t, routines.filter(r => r.time_of_day === t)]).filter(([, rs]) => rs.length);

  return (
    <div style={styles.root}>
      <h3 style={styles.heading}>Routines</h3>
      {groups.map(([t, rs]) => (
        <div key={t} style={styles.group}>
          <p style={styles.groupLabel}>{TIME_LABELS[t]}</p>
          {rs.map(r => (
            <RoutineRow key={r.id} routine={r}
              onSave={u => setRoutines(prev => prev.map(x => (x.id === u.id ? u : x)))}
              onDelete={id => setRoutines(prev => prev.filter(x => x.id !== id))} />
          ))}
        </div>
      ))}
      {adding
        ? <RoutineForm initial={EMPTY} onSubmit={add} onCancel={() => setAdding(false)} submitLabel="Add routine" saving={saving} />
        : <button style={styles.addBtn} onClick={() => setAdding(true)}>+ Add routine</button>}
    </div>
  );
}

const styles = {
  root: { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  heading: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-tertiary)", paddingBottom: "var(--space-2)", borderBottom: "1px solid var(--color-border)" },
  group: { display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  groupLabel: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)", margin: 0 },
  row: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)", display: "flex", alignItems: "center", gap: "var(--space-4)" },
  rowTitle: { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", margin: 0 },
  rowMeta: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: 0 },
  actions: { display: "flex", gap: "var(--space-2)", flexShrink: 0 },
  editBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "2px var(--space-3)", cursor: "pointer" },
  deleteBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer" },
  editCard: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  twoCol: { display: "flex", gap: "var(--space-4)" },
  field: { display: "flex", flexDirection: "column", gap: "var(--space-1)", flex: 1 },
  label: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", color: "var(--color-text-tertiary)" },
  input: { font: "inherit", fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%", boxSizing: "border-box" },
  textarea: { font: "inherit", fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%", resize: "vertical", boxSizing: "border-box" },
  steps: { display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  stepRow: { display: "flex", alignItems: "flex-start", gap: "var(--space-2)" },
  stepNum: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", width: 16, paddingTop: 8, textAlign: "right" },
  stepFields: { flex: 1, display: "flex", flexDirection: "column", gap: 4 },
  stepBtns: { display: "flex", gap: 2, paddingTop: 4 },
  tiny: { font: "inherit", fontSize: "var(--text-xs)", width: 24, height: 24, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", background: "none", color: "var(--color-text-secondary)", cursor: "pointer" },
  addStep: { font: "inherit", fontSize: "var(--text-xs)", color: "var(--color-accent)", background: "none", border: "none", cursor: "pointer", alignSelf: "flex-start", padding: 0 },
  editActions: { display: "flex", gap: "var(--space-3)" },
  saveBtn: { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  cancelBtn: { font: "inherit", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  addBtn: { font: "inherit", fontSize: "var(--text-sm)", color: "var(--color-accent)", background: "var(--color-accent-subtle)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer", alignSelf: "flex-start" },
};
